import { getApiOrigin } from "@utils/apiOrigin";

export interface HistoricPenaltiesAccordionSettingsResponse {
  success?: boolean;
  message?: string;
  response?: {
    enabled?: boolean;
  };
}

export interface HistoricPenaltiesAccordionSettings {
  enabled: boolean;
}

const HISTORIC_PENALTIES_ACCORDION_SETTINGS_PATH =
  "/controls/historic-penalties-accordion/settings";

let cachedSettings: HistoricPenaltiesAccordionSettings | null = null;
let pendingSettingsPromise: Promise<HistoricPenaltiesAccordionSettings> | null =
  null;

/**
 * Whether the rules server shows the Historic Penalties accordion on Potential Penalties
 * (Discover + Hub). Default true when the endpoint is unreachable.
 */
export async function getHistoricPenaltiesAccordionSettings(
  forceRefresh = false,
): Promise<HistoricPenaltiesAccordionSettings> {
  if (!forceRefresh && cachedSettings) {
    return cachedSettings;
  }

  if (!forceRefresh && pendingSettingsPromise) {
    return pendingSettingsPromise;
  }

  pendingSettingsPromise = (async () => {
    const baseUrl = getApiOrigin();
    const candidateUrls: string[] = [];
    const addCandidate = (url: string) => {
      if (!candidateUrls.includes(url)) {
        candidateUrls.push(url);
      }
    };

    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      addCandidate(`${normalizedBase}${HISTORIC_PENALTIES_ACCORDION_SETTINGS_PATH}`);
      addCandidate(
        `${normalizedBase}/api/v1${HISTORIC_PENALTIES_ACCORDION_SETTINGS_PATH}`,
      );
    }

    addCandidate(HISTORIC_PENALTIES_ACCORDION_SETTINGS_PATH);
    addCandidate(`/api/v1${HISTORIC_PENALTIES_ACCORDION_SETTINGS_PATH}`);

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          continue;
        }
        const data =
          (await response.json()) as HistoricPenaltiesAccordionSettingsResponse;
        const settings: HistoricPenaltiesAccordionSettings = {
          enabled: data?.response?.enabled !== false,
        };
        cachedSettings = settings;
        return settings;
      } catch {
        // Try next candidate URL.
      }
    }

    return { enabled: true };
  })();

  try {
    return await pendingSettingsPromise;
  } finally {
    pendingSettingsPromise = null;
  }
}
